import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { VendorNote } from "@/types/negotiations";

function queryKey(vendor_name: string) {
  return ["vendor_notes", vendor_name] as const;
}

async function fetchNotes(vendor_name: string): Promise<VendorNote[]> {
  const { data, error } = await supabase
    .from("vendor_notes")
    .select("*")
    .eq("vendor_name", vendor_name)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/** Hook for per-vendor notes (create / edit / delete). */
export function useVendorNotes(vendor_name: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const key = queryKey(vendor_name);

  const {
    data: notes = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: key,
    queryFn: () => fetchNotes(vendor_name),
    enabled: !!vendor_name,
  });

  const addNoteMutation = useMutation({
    mutationFn: async (body: string): Promise<VendorNote> => {
      if (!body.trim()) throw new Error("Note body cannot be empty");

      const { data, error } = await supabase
        .from("vendor_notes")
        .insert({ vendor_name, body: body.trim() })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast({ title: "Note added" });
    },
    onError: (err) => {
      toast({
        title: "Failed to add note",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: string }): Promise<VendorNote> => {
      if (!body.trim()) throw new Error("Note body cannot be empty");

      const { data, error } = await supabase
        .from("vendor_notes")
        .update({ body: body.trim() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast({ title: "Note updated" });
    },
    onError: (err) => {
      toast({
        title: "Failed to update note",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from("vendor_notes").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast({ title: "Note deleted" });
    },
    onError: (err) => {
      toast({
        title: "Failed to delete note",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  return {
    notes,
    isLoading,
    error,
    refetch,

    /** Append a new note for this vendor. */
    addNote: (body: string) => addNoteMutation.mutateAsync(body),
    isAdding: addNoteMutation.isPending,
    addNoteError: addNoteMutation.error as Error | null,

    /** Update the body of an existing note by id. */
    updateNote: (id: string, body: string) => updateNoteMutation.mutateAsync({ id, body }),
    isUpdating: updateNoteMutation.isPending,
    updatingNoteId: updateNoteMutation.isPending
      ? ((updateNoteMutation.variables as { id: string; body: string } | undefined)?.id ?? null)
      : null,
    updateNoteError: updateNoteMutation.error as Error | null,

    /** Delete a note by id. */
    deleteNote: (id: string) => deleteNoteMutation.mutateAsync(id),
    isDeleting: deleteNoteMutation.isPending,
    deletingNoteId: deleteNoteMutation.isPending
      ? ((deleteNoteMutation.variables as string | undefined) ?? null)
      : null,
    deleteNoteError: deleteNoteMutation.error as Error | null,
  };
}
